import { assign, fromPromise, setup } from 'xstate'
import {
  CreateUserResponse,
  AssociateRetailUserResponse,
} from '@tovala/browser-apis-combinedapi'

export interface NameFormData {
  firstName: string
  lastName: string
  phoneNumber?: string
}

export interface AccountFormData {
  email: string
  password: string
}

export interface RetailChannelData {
  retailChannel?: string
  location?: string
}

export interface RegistrationContext {
  info: NameFormData & AccountFormData & RetailChannelData
  userID: number | undefined
}

export type RegistrationEvents =
  | {
      formData: {
        email: string
        password: string
        retailChannel?: string
        location?: string
      }
      type: 'ACCOUNT_DETAILS_ENTERED'
    }
  | { type: 'BACK' }
  | {
      formData: {
        firstName: string
        lastName: string
        retailChannel?: string
        location?: string
      }
      type: 'NAME_ENTERED'
    }

type RegistrationActors = {
  submitRegistration: { data: CreateUserResponse }
  submitAccountAssociation: { data: AssociateRetailUserResponse }
}

const INITIAL_CONTEXT = {
  info: {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    retailChannel: undefined,
    location: undefined,
  },
} satisfies Pick<RegistrationContext, 'info'>

export const registrationMachine = setup({
  types: {
    context: {} as RegistrationContext,
    events: {} as RegistrationEvents,
  },
  actions: {
    markRegistrationComplete: () => {
      throw new Error('markRegistrationComplete not implemented')
    },
    redirectToOrders: () => {
      throw new Error('redirectToOrders not implemented')
    },
    redirectToAppDownload: () => {
      throw new Error('redirectToAppDownload not implemented')
    },
    removeCurrentToken: () => {
      throw new Error('removeCurrentToken not implemented')
    },
    saveNewToken: (_, params: { token: string }) => {
      throw new Error(
        `saveNewToken not implemented. Expected params: ${Object.keys(params)}`
      )
    },
    trackDoneName: () => {
      throw new Error('trackDoneName not implemented')
    },
    trackSubmitRegistration: () => {
      throw new Error('trackSubmitRegistration not implemented')
    },
    trackUserRegistered: () => {
      throw new Error('trackUserRegistered not implemented')
    },
    trackUserAsscoiatedToRetailChannel: (
      _,
      params: {
        location: string
        retailChannel: string
        phoneNumber: string
        userID: number | undefined
      }
    ) => {
      throw new Error(
        `trackUserAsscoiatedToRetailChannel not implemented. Expected params: ${Object.keys(
          params
        )}`
      )
    },
    storeData: assign(
      ({ context }, params: { formData: NameFormData | AccountFormData }) => {
        return { ...context, info: { ...context.info, ...params.formData } }
      }
    ),
    storeUserID: assign(({ context }, params: { userID: number }) => {
      return { ...context, userID: params.userID }
    }),
  },
  actors: {
    submitRegistration: fromPromise<
      CreateUserResponse,
      {
        info: RegistrationContext['info']
      }
    >(async () => {
      throw new Error('submitRegistration not implemented')
    }),
    submitAccountAssociation: fromPromise<
      AssociateRetailUserResponse,
      {
        info: RegistrationContext['info']
        userID: number | undefined
      }
    >(async () => {
      throw new Error('submitAccountAssociation not implemented')
    }),
  },
  guards: {
    hasRetailChannelInfo: ({ context }) => {
      return (
        !!(context.info.retailChannel || context.info.location) &&
        context.userID !== undefined
      )
    },
  },
}).createMachine({
  types: {
    actors: {} as RegistrationActors,
  },
  context: () => ({
    ...INITIAL_CONTEXT,
    userID: undefined,
  }),
  id: 'registration',
  initial: 'enteringName',
  states: {
    enteringName: {
      on: {
        NAME_ENTERED: {
          actions: [
            {
              type: 'storeData',
              params: ({ event }) => ({ formData: event.formData }),
            },
            'trackDoneName',
          ],
          target: 'enteringAccountDetails',
        },
      },
    },
    enteringAccountDetails: {
      on: {
        ACCOUNT_DETAILS_ENTERED: {
          actions: [
            {
              type: 'storeData',
              params: ({ event }) => ({ formData: event.formData }),
            },
          ],
          target: 'registering',
        },
        BACK: { target: 'enteringName' },
      },
    },
    registering: {
      // We remove the user's current token because they may be registering for
      // a new account. So when they submit their registration we'll authenticate
      // as the newly created account or the user will be logged out and will have
      // to choose which account to log back in to. Note: This is only relevant since
      // an authenticated user can register for a new account.
      entry: ['removeCurrentToken', 'trackSubmitRegistration'],
      invoke: {
        id: 'submitRegistration',
        src: 'submitRegistration',
        input: ({ context }: { context: RegistrationContext }) => ({
          info: context.info,
        }),
        onDone: [
          {
            actions: [
              {
                type: 'saveNewToken',
                params: ({ event }) => ({ token: event.output.token }),
              },
              'markRegistrationComplete',
              'trackUserRegistered',
              {
                type: 'storeUserID',
                params: ({ event }) => ({ userID: event.output.user.id }),
              },
              {
                type: 'trackUserAsscoiatedToRetailChannel',
                params: ({ context }) => ({
                  location: context.info.location || '',
                  retailChannel: context.info.retailChannel || '',
                  phoneNumber: context.info.phoneNumber || '',
                  userID: context.userID,
                }),
              },
            ],
            guard: 'hasRetailChannelInfo',
            target: 'updatingRetailChannel',
          },
          {
            actions: [
              {
                type: 'saveNewToken',
                params: ({ event }) => ({ token: event.output.token }),
              },
              'markRegistrationComplete',
              'trackUserRegistered',
            ],
            target: 'registered',
          },
        ],
        onError: {
          target: 'enteringAccountDetails',
        },
      },
    },
    updatingRetailChannel: {
      invoke: {
        id: 'submitAccountAssociation',
        src: 'submitAccountAssociation',
        input: ({ context }) => ({
          info: context.info,
          userID: context.userID,
        }),
        onDone: {
          target: 'registeredAsRetailUser',
        },
        onError: {
          target: 'registeredAsRetailUser',
        },
      },
    },
    registered: { entry: ['redirectToOrders'], type: 'final' },
    registeredAsRetailUser: {
      entry: ['redirectToAppDownload'],
      type: 'final',
    },
  },
})
